import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { AlertsTabIcon, MarkerIcon, SMSIconFilled } from '@rsos/assets/icons';
import { INTERAGENCY_CHAT } from '@rsos/capstone/src/constants/capabilities';
import AlertsBadge from '@rsos/components/capstone/Queue/AlertsBadge';
import AlertsQueue from '@rsos/components/capstone/Queue/AlertsQueue';
import EmergencyCallsBadge from '@rsos/components/capstone/Queue/EmergencyCallsBadge';
import { Box } from '@rsos/components/capstone/base';
import { withLayout } from '@rsos/components/capstone/base/Layout';
import { DISPATCH_REQUESTED } from '@rsos/constants/alertStatuses';
import {
  ACTIVE_ASSAILANT,
  TRAIN_INCIDENT_ALERT_TYPE,
} from '@rsos/constants/alertTypes';
import { EMERGENCY_DATA } from '@rsos/constants/appNames';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import {
  AGENCY_CHAT,
  ALERTS,
  EMERGENCY_CALLS,
} from '@rsos/constants/incidentTypes';
import { removeAlert } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { toggleQueueCollapse } from '@rsos/reduxDataStore/src/features/queue/queueSlice';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import getOldestRemovableAlert from '@rsos/reduxDataStore/src/utils/getOldestRemovableAlert';
import {
  APP_NAME,
  getRspAlertQueueMaxSize,
  readMetaTag,
} from '@rsos/utils/metaTags';
import useIsIRP from '@rsos/utils/useIsIRP';
import ConditionalFeatureDisplay from '../Common/ConditionalFeatureDisplay/ConditionalFeatureDisplay';
import AgencyChatBadge from '../InteragencyChat/AgencyChatBadge/AgencyChatBadge';
import AgencyConversationsList from './AgencyConversationsList/AgencyConversationsList';
import AllEvents from './AllEvents';
import EmergencyCallsQueue from './EmergencyCallsQueue';
import {
  Container,
  Content,
  IconWrapper,
  Tab,
  TabLabel,
  Tabs,
  QueueBorderTabs,
} from './Queue.styles';

const isRSP = readMetaTag(APP_NAME) === EMERGENCY_DATA;

const LayoutContainer = withLayout(Container);

const ALERT_QUEUE_MAX_SIZE = getRspAlertQueueMaxSize();

/**
 * @param {String} activeIncidentID - selectedCallerID or selectedAlertIDh
 * @param {String} activeQueueTab - ALERTS or EMERGENCY_CALLS
 * @param {Object} filteredCalls - callQueue, but filtered for only calls with
 * locations
 * @param {boolean} hasAgent511Access - does user have access to view Agent511 text-to-911 requests?
 * @param {boolean} hasAccessToInteragencyChat - does user have access to view interagency chat?
 * @param {boolean} hasAlertsAccess - does user have access to view alerts?
 * @param {boolean} isEmergencyCallsEnabled - is 911 emergency call queue enabled?
 * @param {boolean} isJVEnabled - is JV enabled account
 * @param {Function} onQueueHover - Hover effect handler for markers
 * @param {Function} onQueueTabClick - function to set the active queue tab
 * @param {Function} onRemoveID - function to set state in JV for removed alert
 * or caller ID for use with the Google Maps
 * @param {String} selectedAlertID - the selected alert ID for the alerts queue
 * @param {String} selectedCallerID - the selected caller ID for the 911 emergency call queue
 * @param {Function} setRemovedAlertID - sets the state in `JurisdictionView`
 * to trigger removing an alert's marker
 * @param {Function} setShowPane - function to show/hide the detail pane
 * @param {Class<Hermes>} WSInstance - Hermes websocket instance
 * @param {Class<StreamWS>} streamWS- StreamWS websocket instance
 * @param {Function} onViewRcsEvent - function that the rcs toast action button uses to open relevant event
 */
const Queue = ({
  activeIncidentID,
  activeQueueTab = EMERGENCY_CALLS,
  filteredCalls = {},
  hasAccessToInteragencyChat,
  hasAgent511Access = false,
  hasAlertsAccess,
  hoveredCall,
  irpAlertsID,
  isEmergencyCallsEnabled = true,
  onQueueHover = () => {},
  onQueueTabClick,
  onRemoveID,
  onQueueItemClicked,
  setActiveQueueTab,
  selectedAlertID,
  selectedCallerID,
  setRemovedAlertID = () => {},
  setShowPane,
  WSInstance,
  streamWS,
  inactiveIncidentID,
  onViewJurisdiction,
  actionBarRef,
  onViewRcsEvent,
}) => {
  const dispatch = useDispatch();
  const { currentRole, profile } = useSelector(state => state.sinatra.user);
  const alerts = useSelector(state => filteredAlertsSelector(state.incidents));

  const activeAssailantAlerts = useSelector(
    state => state.incidents.activeAssailantAlerts,
  );
  const notifyActiveAssailant =
    !!Object.values(activeAssailantAlerts).find(
      a => a.status.name === DISPATCH_REQUESTED,
    ) && activeQueueTab !== ALERTS;
  const { trainAlerts } = useSelector(state => state.incidents);
  const notifyTrainIncident =
    !!Object.values(trainAlerts).find(
      a => a.status.name === DISPATCH_REQUESTED,
    ) && activeQueueTab !== ALERTS;

  const getNotifyByAlertType = () => {
    return notifyActiveAssailant
      ? ACTIVE_ASSAILANT
      : notifyTrainIncident
        ? TRAIN_INCIDENT_ALERT_TYPE
        : null;
  };
  const isAlertsLoading = useSelector(
    state => state.incidents.loading.allAlerts,
  );
  const isCollapsed = useSelector(state => state.queue?.isQueueCollapsed);

  const { irpVersion, isIRPresponsiveConditional } = useIsIRP();

  const { hasCalls } = useMemo(() => {
    return {
      hasCalls: Object.keys(filteredCalls).length,
    };
  }, [filteredCalls]);

  useEffect(() => {
    // Remove oldest alert. If it is selected, remove the second oldest
    if (Object.keys(alerts).length > ALERT_QUEUE_MAX_SIZE) {
      const removableAlert = getOldestRemovableAlert(alerts, selectedAlertID);
      dispatch(removeAlert(removableAlert));

      if (isRSP)
        if (!activeIncidentID) {
          // When viewing an alert, removedID will not get reset until closing
          // the detailed view, resulting in incorrect marker handling.
          // `removableAlert` can only be the oldest alert and not the second
          // oldest alert when in the all view
          setRemovedAlertID(removableAlert);
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts, dispatch]);

  useEffect(() => {
    const mainContainer = document.getElementById('root');
    const resizeObserver = new ResizeObserver(entry => {
      const newWidth = entry[0]?.contentRect.width;
      if (isCollapsed && newWidth && newWidth < BP_LAPTOP) {
        dispatch(toggleQueueCollapse());
      }
    });
    resizeObserver.observe(mainContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isCollapsed, dispatch]);

  return (
    <LayoutContainer
      activeQueueTab={activeQueueTab}
      isIRP={isIRPresponsiveConditional}
      actionBarHeight={actionBarRef?.current?.clientHeight || 0}
    >
      <Box p={0} m={0}>
        <Tabs>
          {isEmergencyCallsEnabled && (
            <Tab
              active={activeQueueTab === EMERGENCY_CALLS}
              data-name="911calls-tab"
              id={EMERGENCY_CALLS}
              onClick={() => onQueueTabClick(EMERGENCY_CALLS)}
            >
              <IconWrapper type={EMERGENCY_CALLS}>
                <MarkerIcon width={15} height={14} />
              </IconWrapper>

              <TabLabel type={EMERGENCY_CALLS}>911</TabLabel>
              <EmergencyCallsBadge />
            </Tab>
          )}
          {hasAlertsAccess && (
            <Tab
              active={activeQueueTab === ALERTS}
              data-name="Alerts-tab"
              id={ALERTS}
              onClick={() => onQueueTabClick(ALERTS)}
              notifyByAlertType={getNotifyByAlertType()}
            >
              <IconWrapper
                type={ALERTS}
                notifyByAlertType={getNotifyByAlertType()}
              >
                <AlertsTabIcon
                  width={9}
                  height={14}
                  viewboxWidth={9}
                  viewboxHeight={14}
                />
              </IconWrapper>
              <TabLabel
                type={ALERTS}
                notifyByAlertType={getNotifyByAlertType()}
              >
                Alerts
              </TabLabel>
              <AlertsBadge notifyByAlertType={getNotifyByAlertType()} />
            </Tab>
          )}
          {hasAccessToInteragencyChat && (
            <Tab
              active={activeQueueTab === AGENCY_CHAT}
              data-name="agency-chat-tab"
              id={AGENCY_CHAT}
              onClick={() => onQueueTabClick(AGENCY_CHAT)}
            >
              <IconWrapper type={AGENCY_CHAT}>
                <SMSIconFilled width={14} height={14} />
              </IconWrapper>
              <TabLabel type={AGENCY_CHAT}>Agency Chat</TabLabel>
              <AgencyChatBadge />
            </Tab>
          )}
        </Tabs>
      </Box>
      <>
        <QueueBorderTabs />
        <AllEvents
          activeQueueTab={activeQueueTab}
          selectedCallerID={selectedCallerID}
          hasCalls={hasCalls}
          onViewJurisdiction={onViewJurisdiction}
        />
      </>

      {isEmergencyCallsEnabled && (
        <Content
          active={activeQueueTab === EMERGENCY_CALLS}
          activeQueueTab={activeQueueTab}
          data-name="calls-queue"
          hasSingleTab={!hasAlertsAccess}
        >
          <EmergencyCallsQueue
            emergencyCalls={filteredCalls}
            hasAgent511Access={hasAgent511Access}
            hoveredCall={hoveredCall}
            isVisible={activeQueueTab === EMERGENCY_CALLS}
            onQueueHover={onQueueHover}
            onRemoveID={onRemoveID}
            selectedCallerID={selectedCallerID}
            irpVersion={irpVersion}
            onQueueItemClicked={onQueueItemClicked}
            onViewRcsEvent={onViewRcsEvent}
          />
        </Content>
      )}

      {hasAlertsAccess && (
        <Content
          data-name="alerts-queue"
          activeQueueTab={activeQueueTab}
          active={activeQueueTab === ALERTS}
        >
          <AlertsQueue
            data-name="alerts-queue"
            activeIncidentID={activeIncidentID}
            inactiveIncidentID={inactiveIncidentID}
            alerts={alerts}
            currentRole={currentRole}
            isLoading={isAlertsLoading}
            isVisible={activeQueueTab === ALERTS}
            onQueueHover={onQueueHover}
            onRemoveID={onRemoveID}
            setActiveQueueTab={setActiveQueueTab}
            selectedCallerID={selectedCallerID}
            selectedAlertID={selectedAlertID}
            setShowPane={setShowPane}
            userProfile={profile}
            WSInstance={WSInstance}
            streamWS={streamWS}
            irpAlertsID={irpAlertsID}
            onQueueItemClicked={onQueueItemClicked}
          />
        </Content>
      )}

      <ConditionalFeatureDisplay
        dataName="agency-chat-queue"
        conditions={[{ capabilityEnabled: INTERAGENCY_CHAT }]}
      >
        <Content
          data-name="agency-chat-queue"
          activeQueueTab={activeQueueTab}
          active={activeQueueTab === AGENCY_CHAT}
        >
          <AgencyConversationsList />
        </Content>
      </ConditionalFeatureDisplay>
    </LayoutContainer>
  );
};

Queue.propTypes = {
  activeQueueTab: PropTypes.string,
  hasAgent511Access: PropTypes.bool,
  hasAlertsAccess: PropTypes.bool,
  isEmergencyCallsEnabled: PropTypes.bool,
  isJVEnabled: PropTypes.bool,
  filteredCalls: PropTypes.object,
  onQueueHover: PropTypes.func,
  setShowPane: PropTypes.func,
};

export default Queue;
